/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { v4 as uuid } from 'uuid';
import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import type { RemoteNavigationView } from './TypesRemoteViewRemover.tsx';
import { VIEW_NAME_MAX_LENGTH } from './container.constants.tsx';
import type { State, ContainerState, NavigationView } from './NavigationView.types.tsx';

export const updateContainerState = (
	state: State,
	containerId: string,
	containerState: Partial<ContainerState>,
): State => ({
	...state,
	containers: {
		...state.containers,
		[containerId]: {
			...state.containers[containerId],
			...containerState,
		},
	},
});

export const modifyView = (
	state: State,
	containerId: string,
	modify: (view: NavigationView) => NavigationView,
): State =>
	updateContainerState(state, containerId, {
		views: state.containers[containerId].views.map((view) => {
			const newView = modify(view);
			if (newView === view && view.views) {
				return {
					...newView,
					views: view.views?.map(modify),
				};
			}
			return newView;
		}),
	});

export const findViewBy = (
	containerState: ContainerState,
	by: (view: NavigationView) => boolean,
): { view?: NavigationView; parent?: NavigationView } => {
	let found = containerState.views.find((view) => by(view));
	if (!found) {
		let parent;
		containerState.views.some((view) => {
			if (view.views) {
				return view.views.some((innerView) => {
					if (by(innerView)) {
						found = innerView;
						parent = view;
						return true;
					}
					return false;
				});
			}
			return false;
		});
		return { view: found, parent };
	}
	return { view: found, parent: undefined };
};

export const findView = (
	containerState: ContainerState,
	localId: LocalViewId,
): { view?: NavigationView; parent?: NavigationView } =>
	findViewBy(containerState, (view) => view.localId === localId);

export const transformRemoteView = (remoteView: RemoteNavigationView): NavigationView => ({
	...remoteView,
	views: remoteView.views?.map(transformRemoteView),
	localId: uuid(),
	isExpanded: false,
});

const FAKE_ID_PREFIX = 'fake';
export const createFakeId = (name?: string): string => `${FAKE_ID_PREFIX}-${name}-${uuid()}`;
export const isFakeId = (id: string): boolean => id.startsWith(FAKE_ID_PREFIX);

export const makeNewTitle = (titleSeed?: string): string => {
	const suffix = ' Copy';
	return titleSeed !== undefined
		? `${titleSeed.substr(0, VIEW_NAME_MAX_LENGTH - suffix.length)}${suffix}`
		: 'New View';
};
